import { fbt } from 'fbtee'
import { MapPin, Plus, X } from 'lucide-react'
import { PREGAME_ADDRESS_MAX, PREGAME_PLACE_NAME_MAX, savedPlaceKey, type SavedPlace } from '@shared/pregames'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { useAuth } from '../hooks/useAuth'
import { cn } from '../lib/utils'

export interface PlaceValue {
  placeName: string
  address: string
}

const FIELD_LABEL = 'pt-1 text-xs font-normal text-muted-foreground'
const CHIP = 'inline-flex max-w-full items-center rounded-full border text-xs'

/** A pre-game's place: one-tap chips for places used before, then the name + address fields. */
export function PregamePlaceFields({ value, onChange, savedPlaces, onRemoveSaved }: {
  value: PlaceValue
  onChange: (value: PlaceValue) => void
  savedPlaces: SavedPlace[]
  onRemoveSaved: (place: SavedPlace) => void
}) {
  const currentKey = savedPlaceKey({ name: value.placeName, address: value.address })
  const isEmpty = !value.placeName.trim() && !value.address.trim()
  const firstName = useAuth().user?.displayName?.trim().split(/\s+/)[0]

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{fbt('Where', 'Label of the place section in the pre-game form')}</p>
      {savedPlaces.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="saved-places">
          {savedPlaces.map(p => {
            const selected = savedPlaceKey(p) === currentKey
            const label = p.name || p.address
            return (
              <span
                key={savedPlaceKey(p)}
                className={cn(CHIP, selected ? 'border-primary bg-primary/10 text-foreground' : 'text-muted-foreground')}
              >
                <button
                  type="button"
                  className="flex min-w-0 items-center gap-1 py-1 pl-2.5 pr-1 cursor-pointer"
                  onClick={() => onChange({ placeName: p.name ?? '', address: p.address ?? '' })}
                >
                  <MapPin className="size-3 shrink-0" />
                  <span className="truncate">{label}</span>
                </button>
                <button
                  type="button"
                  className="rounded-full py-1 pl-0.5 pr-2 hover:text-foreground cursor-pointer"
                  onClick={() => onRemoveSaved(p)}
                  aria-label={fbt(
                    'Forget ' + fbt.param('place', label ?? ''),
                    'Aria label on the x of a saved pre-game place chip that removes it from the list',
                  )}
                >
                  <X className="size-3" />
                </button>
              </span>
            )
          })}
          <button
            type="button"
            className={cn(CHIP, 'gap-1 px-2.5 py-1 cursor-pointer', isEmpty ? 'border-primary bg-primary/10 text-foreground' : 'text-muted-foreground')}
            onClick={() => onChange({ placeName: '', address: '' })}
          >
            <Plus className="size-3" />
            {fbt('Other place', 'Chip in the pre-game form that clears the place fields to type a new place')}
          </button>
        </div>
      )}
      <Label htmlFor="pregame-place-name" className={FIELD_LABEL}>
        {fbt('Name', 'Label of the place-name field in the pre-game form — what the host calls the place, remembered for next time')}
      </Label>
      <Input
        id="pregame-place-name"
        value={value.placeName}
        onChange={e => onChange({ ...value, placeName: e.target.value })}
        maxLength={PREGAME_PLACE_NAME_MAX}
        placeholder={firstName
          ? fbt(
            fbt.param('name', firstName) + "'s place",
            'Placeholder of the place-name field in the pre-game form — the host’s own first name as an example of what people call a place',
          )
          : fbt("Sam's place", 'Placeholder of the place-name field in the pre-game form when the host has no name — an example of what people call a place')}
      />
      <Label htmlFor="pregame-address" className={FIELD_LABEL}>
        {fbt('Address', 'Label of the address field in the pre-game form')}
      </Label>
      <Input
        id="pregame-address"
        value={value.address}
        onChange={e => onChange({ ...value, address: e.target.value })}
        maxLength={PREGAME_ADDRESS_MAX}
        placeholder={fbt('123 Main St', 'Placeholder of the address field in the pre-game form — a generic example street address')}
      />
    </div>
  )
}
